// Right-rail summary panel — renders the current spec as a customer
// would read it back. Used by the QuoteEdit configurator.
//
// Pulls enum labels from the active schema so values render as
// "8.5″ × 11″" rather than the machine code "8.5x11". A custom size reads
// back with the dimensions the customer typed inline ("Custom size — 5.25 × 8.25").

import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  humanizeToken,
  readMediaDecoration,
  INSERTS_CONFIG_KEY,
  INSERTS_TRIGGER_KEY,
  retiredOptionLabel,
  CUSTOM_PACKAGING_BOARD_OTHER_KEY,
  CUSTOM_PACKAGING_REPEAT_NUMBER_KEY,
  CUSTOM_PACKAGING_STOCK_CATALOG_NUMBER_KEY,
  CUSTOM_PACKAGING_STOCK_SUPPLIER_KEY,
  CUSTOM_PACKAGING_STYLE_OTHER_KEY,
  MEDIA_DECORATION_CONFIG_KEY,
  MEDIA_PACKAGING_OTHER_KEY,
  MEDIA_PACKAGING_STOCK_ITEM_KEY,
  MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY,
  MEDIA_PRINT_SIDES_CONFIG_KEY,
  MEDIA_SUPPLY_SOURCE_CONFIG_KEY,
  OPTICAL_MEDIA_TYPES,
  readLineProofType,
  SHEET_USE_CONFIG_KEY,
  type AssemblySnapshot,
  type FormFieldDto,
  type FormSchemaDto,
} from 'shared';

import {
  evalExpr,
  isFinishedSizeField,
  optionAlwaysSelected,
  type FieldValues,
} from './SchemaForm.js';
import { familyLabel, type FamilyCode } from './FamilyGlyph.js';
import { insertsSummaryText } from './InsertsEditor.js';
import { useAssemblyRecap } from './KittingFields.js';
import { ProjectMockup } from './ProjectMockup.js';
import { formatCustomSize, parseCustomSize } from './formatCustomSize.js';
import { formatNumber } from '../i18n/formatters.js';

type Props = {
  schema: FormSchemaDto;
  values: FieldValues;
  title: string;
  quantity: number;
  // Live artwork thumbnail (from useArtworkPreview). When present, the right-rail
  // mockup wears the customer's actual file — on the book cover for booklets, on
  // the flat sheet for single sheet, and in place of the carton for an assembly
  // kit's finished-kit photo; otherwise it shows the branded per-type
  // placeholder. Omitted/null for families with no paint-on face (labels, USB).
  artworkUrl?: string | null;
  // 'rail' (the default) is the sticky right-hand column of one item's
  // configurator. 'strip' is the same read-back sized to sit N-across in the
  // pre-submit review row at the foot of the order (ProjectSummaryStrip): no
  // mockup, and the card chrome comes from the wrapper instead of this panel so
  // the review row doesn't stack a second bordered surface inside one.
  variant?: 'rail' | 'strip';
  // Prefixed onto the family line — "Item 3 · Paperback book" — so a card in
  // the review row says which item it is, in the same numbering the
  // missing-answer summary cites.
  eyebrow?: string;
  // An `assembly` item's kit, shape-checked off its config by the caller. Its
  // spec is the kit rather than schema fields, so the rail reads it back through
  // useAssemblyRecap, the one kit readback builder. Omitted for every other
  // family.
  assembly?: AssemblySnapshot;
};

const NO_ASSEMBLY: AssemblySnapshot = {};

// Order matters — these are the keys we surface, in the order they
// read most naturally in plain English. Keys with no value for the current
// family are skipped, so flat work reads its shared specs and books read the
// cover block then the inside-pages block (v3 cover/interior split).
const SUMMARY_KEYS = [
  // Flat-work shared specs (single_sheet, labels). Ordered to MATCH the form's
  // consolidated flat section (step 17 — the `guts` section for flat work):
  // size → ink → bleed → paper. print_ink (the collapsed color+sidedness pick
  // that replaced print_color + print_sides) reads back as one printer-notation
  // row ("Ink: 4/4"); interior_paper (the shared paper key) reads back LAST via
  // the inside-pages block below — after has_bleed — so flat work surfaces paper
  // at the end, exactly like a book's interior block.
  'finished_size',
  // sheet_orientation reads back right under the size it pairs with (single
  // sheet only; absent/skipped for every other family). "Orientation: Landscape".
  'sheet_orientation',
  'print_ink',
  'has_bleed',
  // Cover block (book cover-bearing families). Ordered to MATCH the form's
  // Cover step (seed field order): binding → size → ink → bleed → paper →
  // finish. cover_binding_style (step 11, perfect-bound only) leads the block so
  // a hardcover/softcover pick reads back first; cover_ink (the collapsed
  // color+sidedness pick that replaced cover_color + cover_print_sides) reads
  // back as one printer-notation row ("Cover ink: 4/4"); cover_finish
  // (lamination) moved to seed order 190 — after cover_paper (180) — so the
  // finish reads back last here.
  'cover_binding_style',
  'cover_finished_size',
  'cover_ink',
  'cover_bleed',
  'cover_paper',
  'cover_finish',
  // Self-cover (book families whose cover prints on the first/last interior
  // sheet — no separate cover stock). A boolean; shown only when TRUE (see
  // BOOLEAN_ONLY_WHEN_TRUE) so a plain "No" never adds noise to the cover block.
  'self_cover',
  // "Cover and inside pages in one file" — an opt-in boolean like self_cover
  // above, so it reads back only when TRUE. It belongs in the cover block: it
  // is the choice that collapses the cover + interior artwork into one upload.
  'combined_artwork',
  // Inside-pages / guts block (book families). Ordered to MATCH the form's
  // Inside pages step (seed field order): page count → size → ink → bleed →
  // paper. interior_paper is the last question in the section (seed order 150).
  // Its input lives in the `guts` step ("Inside pages" for books, "Paper" for
  // flat work — see SchemaForm SECTION_LABEL_BY_FAMILY), so its summary label is
  // family-aware (INSIDE_PAGES_RELABEL below).
  'page_count',
  'interior_finished_size',
  // interior_ink — the collapsed interior color+sidedness (4/4, 4/0, 1/1, 1/0,
  // mixed), the twin of cover_ink above.
  'interior_ink',
  // Color-page count — present in config ONLY when interior_ink = 'mixed' (its
  // conditional_show gate), so it reads back right beneath the ink row for a
  // mixed book and is simply absent otherwise.
  'interior_color_page_count',
  'interior_bleed',
  'interior_paper',
  // (Flat ink — print_ink — moved up into the flat-work block at the top for
  // step-17 section parity; books spec ink per section via the cover_/interior_
  // keys above.)
  'binding',
  'coil_color',
  'coil_size',
  // Flat-work lamination (single_sheet on a 10pt+ card stock) — the flat
  // counterpart to cover_finish, which reads back in the cover block above.
  // Books never set it (they use cover_finish), so it's skipped for them; for a
  // single sheet it reads back right before the finishing operations.
  'lamination',
  // NOTE — the finishing selections do NOT read back as `<dl>` rows any more.
  // `finishing` (in-house ops), `specialty_finishing` (vendor-routed ops) and
  // `proof_type` are rendered below as titled TAG GROUPS (see TAG_GROUPS), so
  // the rail reads back in the same three-section shape the form uses
  // (Finishing touches → Specialty Finishing → Proof) instead of a mix of
  // labeled rows and orphan pills. The step-7 specialty DIRECTION sub-fields
  // (emboss_/foil_ description, location, size + variable_data_description) and
  // the variable-data spreadsheet attachment stay OUT of the summary — the tag
  // already reflects the choice and the free-text/asset state reads as detail,
  // not a crisp spec.
  // Media block, in the order the form asks it: format → capacity → decoration →
  // the decoration follow-up → packaging. Decoration and its follow-up read
  // together because the second answers the first; putting packaging between
  // them (as the pre-FB-0075 order did for the retired boolean) split one answer
  // in two. The follow-up is sidedness on a drive and the PRESS on a disc — they
  // are gated to opposite media_types, so only ever one of the two rows appears.
  'media_type',
  'media_capacity',
  MEDIA_SUPPLY_SOURCE_CONFIG_KEY,
  'media_decoration',
  'media_print_sides',
  'media_print_method',
  'media_packaging',
  // The two packaging ESCAPES read back directly under the pick that revealed
  // them — "Packaging: Packaging we stock" alone names no carrier. Each is a
  // conditional_show field, so only the one the customer actually took is ever
  // set, and neither shows for a named carrier.
  MEDIA_PACKAGING_STOCK_ITEM_KEY,
  MEDIA_PACKAGING_OTHER_KEY,
  // "Something else" block. The free-text request itself stays OUT — it is a
  // paragraph, not a spec row, and the rail would either clip it or swamp the
  // panel; these three are the crisp details beside it. Gated to the family by
  // their own conditional_show, so they never surface on a scoped item.
  'something_else_repeat_number',
  'something_else_size',
  'something_else_stock_number',
  // Custom packaging block, in the order the form asks it. Each `_other` box reads back
  // directly under the pick that revealed it, as the packaging escapes do.
  'custom_packaging_source',
  CUSTOM_PACKAGING_STOCK_SUPPLIER_KEY,
  CUSTOM_PACKAGING_STOCK_CATALOG_NUMBER_KEY,
  'custom_packaging_dimensions',
  'custom_packaging_style',
  CUSTOM_PACKAGING_STYLE_OTHER_KEY,
  'custom_packaging_board',
  CUSTOM_PACKAGING_BOARD_OTHER_KEY,
  'custom_packaging_print_color',
  'custom_packaging_print_sides',
  CUSTOM_PACKAGING_REPEAT_NUMBER_KEY,
];

// The review row (variant 'strip') reads back a HEADLINE, not the whole spec.
// Every answer already lives twice on the page — in the item's configurator and
// in its own rail — so a third full copy N times over is what turned the row
// into a second screenful. Its job is narrower: let the customer recognize each
// piece and spot the one that is wrong. So it keeps the few specs that tell two
// similar pieces apart (size, extent, binding, ink, media format), drops the
// finishing / specialty / proof tag groups, and caps the rows so a densely
// specced book and a flat sheet render at the same height. The detail is one
// click away on the item itself.
const STRIP_KEYS = new Set([
  'finished_size',
  'cover_finished_size',
  'interior_finished_size',
  'page_count',
  'binding',
  'print_ink',
  'cover_ink',
  'media_type',
  'media_capacity',
  // An unscoped item has no other spec to tell two of them apart, so all three
  // of its details earn a place in the review row — and three rows sits inside
  // the cap below, so it stays the same height as its neighbours.
  'something_else_repeat_number',
  'something_else_size',
  'something_else_stock_number',
  // Two packaging sizes on one order are two items, so the size is what tells
  // them apart; the catalog number names stock packaging, the style a custom one.
  'custom_packaging_dimensions',
  CUSTOM_PACKAGING_STOCK_CATALOG_NUMBER_KEY,
  'custom_packaging_style',
  'custom_packaging_print_color',
]);
const STRIP_ROW_LIMIT = 4;

// Boolean SUMMARY_KEYS that read back ONLY when set true — a "No" would be pure
// noise for these opt-in flags. They read back as spec rows in their own section
// (self_cover in the cover block).
const BOOLEAN_ONLY_WHEN_TRUE = new Set(['self_cover', 'combined_artwork']);

// The finishing selections read back as three TITLED TAG GROUPS at the foot of
// the rail, in the SAME order and shape the form presents them — "Finishing
// touches" → "Specialty Finishing" → "Proof".
//
// Each group draws from two kinds of field:
//   - enum_keys  — multi_enum fields; each selected option becomes one tag.
//   - flag_keys  — boolean toggles; the field label becomes a tag when TRUE.
// Every tag is ordered by its field's schema `order` (an enum option adds its
// own option order as a tiebreaker) so a group reads back in form order — e.g.
// Finishing touches shows Rounded Corners (430) → Variable Data (434) →
// Shrinkwrapping (450) before the in-house `finishing` ops (510).
//
// Fields deliberately excluded (deprecated / absent from the active schema, so
// findField returns undefined anyway): `envelopes`, `purchased_labels` (now a
// field of an assembly item's kit), `preprinted_insert` (now its own project),
// `custom_paper`, `plastic_overlay`, `media_art_printed` (superseded by the
// `media_decoration` enum). The non-finishing flag that used to ride the pill
// list — `self_cover` — now reads back as a show-only-when-true spec row in its
// own section (see SUMMARY_KEYS).
// `id` is the stable, non-translated lookup key for the render site
// (`t('summary.tagGroup.' + g.id, { defaultValue: g.title })` in SpecSummary) —
// `title` remains the English fallback string, never rendered directly.
const TAG_GROUPS: { id: string; title: string; enum_keys: string[]; flag_keys: string[] }[] = [
  {
    id: 'finishing',
    title: 'Finishing touches',
    enum_keys: ['finishing'],
    flag_keys: [
      'perforate',
      'rounded_corners',
      'variable_print',
      'shrinkwrap',
      'slot_punch',
      'ncr_glue',
      'chipboard_backing',
      'tabs_included',
      INSERTS_TRIGGER_KEY,
    ],
  },
  { id: 'specialty', title: 'Specialty Finishing', enum_keys: ['specialty_finishing'], flag_keys: [] },
  { id: 'proof', title: 'Proof', enum_keys: ['proof_type'], flag_keys: [] },
];

// Presentation-only concise labels for the right rail. The FORM uses the
// schema's verbose, customer-guiding labels ("Print the cover to the edge (full
// bleed)?"); the at-a-glance summary reads back tighter ("Cover bleed"). Any key
// not listed falls back to the schema label — most are already terse enough
// (e.g. "Cover size", "Page Count"), so only the long-form prompts are mapped
// here. This does NOT touch the mastered vocabulary; the schema/seed are
// unchanged.
const SUMMARY_LABELS: Record<string, string> = {
  has_bleed: 'Bleed',
  cover_bleed: 'Cover bleed',
  interior_bleed: 'Inside-pages bleed',
  proof_type: 'Proof',
  // The media pair. The schema labels are already terse, but "Printed sides"
  // alone is ambiguous in a flat rail that also carries a book's cover and
  // inside-pages sides, so both name the thing they sit on.
  media_decoration: 'Disc or drive art',
  media_print_sides: 'Art printed sides',
  media_print_method: 'Art printing method',
  // The schema label is the question ("Who supplies the drives?"); the rail
  // names the thing and lets the option label carry who.
  [MEDIA_SUPPLY_SOURCE_CONFIG_KEY]: 'Drives',
  // Both escapes sit under the "Packaging" row and answer it, so the rail names
  // the carrier rather than repeating the schema's follow-up question ("Which
  // one?" / "What packaging do you need?"), which reads as a prompt with no
  // question above it once the two rows are adjacent.
  [MEDIA_PACKAGING_STOCK_ITEM_KEY]: 'Stocked packaging',
  [MEDIA_PACKAGING_OTHER_KEY]: 'Packaging needed',
};

// Family-aware labels for the two inside-pages rows that DON'T already carry the
// "Inside-pages …" prefix their siblings do — page_count ("Page Count") and
// interior_paper ("Paper / stock"). Prefixing them makes every cover row read
// "Cover …" and every inside-pages row read "Inside-pages …", so the split
// between the cover block (top) and the inside-pages block (bottom) is
// unmistakable at a glance. The other interior keys (size / color / sides /
// bleed) already carry the prefix in their schema (or SUMMARY_LABELS) label, so
// they need no override here.
//
// These two fields are SHARED with flat work (single_sheet, labels), where the
// form renders the `guts` step as a plain "Paper" section with no cover/inside
// distinction — so the prefix would be wrong there. We apply it only for the
// families that actually have an inside-pages section (INSIDE_PAGES_FAMILIES
// below). For book families this relabel takes precedence over SUMMARY_LABELS;
// flat families fall through to SUMMARY_LABELS / the schema label.
const INSIDE_PAGES_RELABEL: Record<string, string> = {
  page_count: 'Inside-pages count',
  interior_paper: 'Inside-pages paper',
  // Reads back right beneath the "Inside-pages ink: Mixed …" row; the schema
  // label is a full question, too long for the rail.
  interior_color_page_count: 'Inside-pages color count',
};

// Families with a distinct "Inside pages" section — the book families. Flat work
// (single_sheet, labels) has a single "Paper" step with no cover/inside split,
// and media_replication has neither, so the "Inside-pages …" prefix would be
// wrong for both. Kept as an explicit allow-list (mirrors the seed's
// `bookFamilies` gating on page_count / interior_paper) rather than "anything
// that isn't flat", so a future non-book family never lands in this bucket by
// default. loose_leaf is included: it has interior pages even though it has no
// printed cover (its cover_* rows are simply absent and skip themselves).
const INSIDE_PAGES_FAMILIES = new Set<FamilyCode>([
  'saddle_stitch_book',
  'perfect_bound_book',
  'hard_bound_book',
  'coil_or_wire_o_book',
  'loose_leaf',
]);

function familyHasInsidePages(family: FamilyCode | null): boolean {
  return family != null && INSIDE_PAGES_FAMILIES.has(family);
}

// Keys whose enum option labels follow a "Short, explanation" shape, where the
// summary wants just the short head ("Yes, print edge-to-edge" → "Yes",
// "Digital proof: we email a PDF to approve" → "Digital proof"). Scoped to
// these keys on purpose: a blanket split would wrongly shorten
// NCR parts ("NCR, 3-part carbonless").
const SHORTEN_VALUE_KEYS = new Set([
  'has_bleed',
  'cover_bleed',
  'interior_bleed',
  'proof_type',
  // sheet_orientation labels are "Portrait: tall" / "Landscape: wide"; the
  // compact rail wants just "Portrait" / "Landscape".
  'sheet_orientation',
  // media_print_sides' double-sided label carries the price signal the picker
  // needs on its face ("Double-sided: a second pass, so it costs more"); the
  // rail wants the pick alone. media_print_method's offset label does the same.
  'media_print_sides',
  'media_print_method',
]);

function findField(schema: FormSchemaDto, key: string): FormFieldDto | undefined {
  return schema.fields.find((f) => f.key === key);
}

// The value to read back for one summary key. Almost always the stored value
// under that key — the exception is `media_decoration`, whose field replaced the
// `media_art_printed` boolean (FB-0075). A pinned or read-only quote written
// before the change stores only the boolean, and the deprecated field is
// filtered out of the served schema, so without this the media block would
// silently lose the row rather than read back what the customer chose.
//
// `readMediaDecoration` is the SAME legacy read the transmit seam uses, so the
// rail and the wire can never describe one stored line differently. It is a pure
// function of already-captured data, not a synthesized intent (R26). The SPA's
// own backfill (SchemaForm) writes the enum forward on the next save; until then
// this is what makes the row survive.
function summaryValue(key: string, values: FieldValues): unknown {
  if (key === MEDIA_DECORATION_CONFIG_KEY) return readMediaDecoration(values);
  // An optical disc has one printable face, so sidedness is a USB-body question
  // and a disc line has no answer to read back. A line stored while the field
  // was offered on every format still carries the key: this loop does not
  // evaluate `conditional_show` (only buildTagGroups does), and
  // scrubConfigForVisibility fires only when the customer next edits a gating
  // key — so without this the rail keeps reporting "Double-sided" on a disc with
  // no control left on the form to clear it. Keyed on the SAME
  // OPTICAL_MEDIA_TYPES membership the seed's gate uses, and a pure read of
  // already-stored values (R26).
  if (key === MEDIA_PRINT_SIDES_CONFIG_KEY) {
    const format = values.media_type;
    if (typeof format === 'string' && (OPTICAL_MEDIA_TYPES as string[]).includes(format)) return undefined;
  }
  // The same trap for drive supply, which the seed asks on `media_type = usb`
  // only: a line moved to a disc by any path that skipped the scrub would
  // otherwise still read "Drives: …" with nothing on the form to clear it.
  if (key === MEDIA_SUPPLY_SOURCE_CONFIG_KEY && values.media_type !== 'usb') return undefined;
  // The same trap for a proof answer stored on a media line that has no printed
  // or etched art (FB-0094): readLineProofType reads none there, as the gates do.
  if (key === 'proof_type' && readLineProofType(values) === null) return undefined;
  // The stocked-packaging pick stores an opaque CompanyItem.item_id; the name the
  // customer picked it by is frozen beside it. Only CompanyItemPicker can turn the
  // key back into a name and it is not mounted here, so the frozen label is what
  // this rail has — falling back to the key for a pick made before the companion
  // existed, which still reads as the item's SKU.
  if (key === MEDIA_PACKAGING_STOCK_ITEM_KEY) {
    const label = values[MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY];
    if (typeof label === 'string' && label.trim() !== '') return label;
  }
  return values[key];
}

// The single-sheet finished-size aspect ratio (width / height) for the right-rail
// mockup, or undefined when no size is chosen / it can't be determined. Reads the
// selected size's trim from the option meta (`dimensions_in`, carried from the
// seed's SIZE_CATALOG into FormFieldEnumOptionDto.meta) and falls back to the
// customer-typed custom dimensions. PRESENTATION ONLY — it shapes the thumbnail,
// never pricing (CLAUDE.md §"Pricing logic lives in the ERP").
//
// The trim fixes the PROPORTION; the `sheet_orientation` toggle fixes which way
// the long edge runs. So we resolve the trim's two side lengths, then orient
// them: portrait ⇒ tall (short/long < 1), landscape ⇒ wide (long/short > 1).
// When orientation is unset (legacy quote, or an untouched picker) we fall back
// to the trim's stored natural orientation so the thumbnail reads exactly as it
// did before the toggle existed.
function sheetAspect(schema: FormSchemaDto, values: FieldValues): number | undefined {
  const f = findField(schema, 'finished_size');
  if (!f) return undefined;
  const v = values['finished_size'];
  if (typeof v !== 'string' || v === '') return undefined;
  let width: number | undefined;
  let height: number | undefined;
  if (v === 'custom') {
    const custom = values['finished_size_custom'];
    const dims = typeof custom === 'string' ? parseCustomSize(custom) : null;
    if (dims) {
      width = dims.width;
      height = dims.height;
    }
  } else {
    const dims = f.enum_options.find((o) => o.value === v)?.meta?.['dimensions_in'];
    if (
      dims != null &&
      typeof dims === 'object' &&
      typeof (dims as { width?: unknown }).width === 'number' &&
      typeof (dims as { height?: unknown }).height === 'number'
    ) {
      width = (dims as { width: number }).width;
      height = (dims as { height: number }).height;
    }
  }
  if (width == null || height == null || width <= 0 || height <= 0) return undefined;
  const orientation = values['sheet_orientation'];
  const long = Math.max(width, height);
  const short = Math.min(width, height);
  if (orientation === 'portrait') return short / long;
  if (orientation === 'landscape') return long / short;
  return width / height;
}

// Trim a verbose option label down to its lead clause for the summary rail,
// keeping only the text before the first gloss separator the seed's option copy
// uses ("Yes, print edge-to-edge" → "Yes", "Portrait: tall" → "Portrait",
// "Digital proof: we email a PDF" → "Digital proof"). Splits on the EARLIEST of
// ", " / ": " (and legacy " — ") so a label carrying both — e.g. "Physical
// proof: a printed sample, mailed to you" — keeps just "Physical proof".
// Returns the input unchanged when it carries no separator.
function shortenValue(value: string): string {
  const idx = [', ', ': ', ' — ']
    .map((sep) => value.indexOf(sep))
    .filter((i) => i !== -1)
    .reduce((min, i) => (min === -1 || i < min ? i : min), -1);
  return idx === -1 ? value : value.slice(0, idx);
}

function displayValue(field: FormFieldDto, value: unknown, shorten = false): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'enum' || field.type === 'multi_enum') {
    // Resolve one option value to its label, shortening per-option when asked.
    // Shortening per-OPTION (not on the joined string) is what keeps a
    // multi-select like proof_type — ['digital','physical'] → "Digital proof,
    // Physical proof" — from being truncated at the first option's em-dash.
    const labelFor = (v: unknown): string => {
      // Prefer the authored option label. A value with no matching option —
      // e.g. a legacy historical value whose option was retired — falls back
      // twice: first to the label that option carried while it WAS offered
      // (RETIRED_OPTION_LABELS, so a historical CD job reads "CD (replicated)"
      // and not "Cd"), then to a humanized token, so a partner never sees raw
      // machine code (`50_offset`) in their own spec summary.
      const lbl =
        field.enum_options.find((o) => o.value === v)?.label ??
        retiredOptionLabel(field.key, String(v)) ??
        humanizeToken(String(v));
      return shorten ? shortenValue(lbl) : lbl;
    };
    if (Array.isArray(value)) {
      // An option the schema marks `meta.always_selected` (the digital proof) is
      // part of every answer whether or not the stored array names it, so union
      // it in — in enum_options order, matching how the picker writes the array.
      // Without this the rail and the ticked-but-locked checkbox disagree on a
      // line saved before the option was locked, or on a read-only line (the
      // configurator's injection pass deliberately skips those). Locking an
      // option also narrows the readback to the OFFERED set, so a retired value
      // that CONTRADICTS the lock — a legacy proof_type of ['none'] — is dropped,
      // not printed beside it. A field with no locked option keeps the
      // verbatim readback, retired values and all.
      const alwaysOn =
        field.type === 'multi_enum'
          ? field.enum_options.filter(optionAlwaysSelected).map((o) => o.value)
          : [];
      const shown =
        alwaysOn.length === 0
          ? value
          : field.enum_options
              .map((o) => o.value)
              .filter((v) => value.includes(v) || alwaysOn.includes(v));
      if (shown.length === 0) return null;
      return shown.map(labelFor).join(', ');
    }
    return labelFor(value);
  }
  if (field.type === 'boolean') return value === true ? i18n.t('common:values.yes') : i18n.t('common:values.no');
  return String(value);
}

// A field surfaces in the rail only when it's visible for the current family —
// we evaluate its conditional_show with the same evalExpr the renderer uses, so
// a retargeted flag (step 9 moved plastic_overlay to coil/wire-o and
// purchased_labels to assembly) never shows for a family it no longer applies
// to, even on an older quote whose stored config still carries the value.
function fieldVisible(field: FormFieldDto, values: FieldValues): boolean {
  return (
    field.conditional_show.length === 0 ||
    field.conditional_show.every((c) => evalExpr(c.expression, values))
  );
}

// Build the titled tag groups (Finishing touches → Specialty Finishing →
// Proof), each as an ordered list of tag labels. A group is omitted entirely
// when it has no active tags, so an empty section never renders a bare heading.
function buildTagGroups(
  schema: FormSchemaDto,
  values: FieldValues,
): { id: string; title: string; tags: string[] }[] {
  const groups: { id: string; title: string; tags: string[] }[] = [];
  for (const group of TAG_GROUPS) {
    // Collect { order, label } so the group reads back in form order regardless
    // of whether a tag came from a flag toggle or a multi_enum option.
    const tags: { order: number; label: string }[] = [];
    for (const key of group.enum_keys) {
      const f = findField(schema, key);
      if (!f || !fieldVisible(f, values)) continue;
      const raw = values[key];
      if (!Array.isArray(raw) || raw.length === 0) continue;
      const shorten = SHORTEN_VALUE_KEYS.has(key);
      // Walk enum_options in form order (display_order) and emit a tag per
      // selected value; the option's own order is a sub-tiebreaker after the
      // field's base order.
      [...f.enum_options]
        .sort((a, b) => a.display_order - b.display_order)
        .forEach((opt, i) => {
          if (!raw.includes(opt.value)) return;
          const label = shorten ? shortenValue(opt.label) : opt.label;
          tags.push({ order: f.display_order + i * 0.001, label });
        });
    }
    for (const key of group.flag_keys) {
      if (values[key] !== true) continue;
      const f = findField(schema, key);
      if (!f || !fieldVisible(f, values)) continue;
      tags.push({ order: f.display_order, label: f.label });
    }
    if (tags.length === 0) continue;
    tags.sort((a, b) => a.order - b.order);
    groups.push({ id: group.id, title: group.title, tags: tags.map((t) => t.label) });
  }
  return groups;
}

export function SpecSummary({
  schema,
  values,
  title,
  quantity,
  artworkUrl,
  variant = 'rail',
  eyebrow,
  assembly,
}: Props): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const kitRecap = useAssemblyRecap(assembly ?? NO_ASSEMBLY);
  // The rail is one complementary panel beside the form; the review row mounts
  // one of these per item, and N unnamed `complementary` landmarks in a document
  // is noise to navigate by, so the strip variant is a plain box.
  const Root = variant === 'strip' ? 'div' : 'aside';
  const family = (values.product_type as FamilyCode | undefined) ?? null;
  const insidePages = familyHasInsidePages(family);
  // Shape the flat-sheet mockup to the chosen finished size (single sheet only;
  // other families ignore it).
  const sheetAspectRatio = family === 'single_sheet' ? sheetAspect(schema, values) : undefined;
  // Which media object the thumbnail draws (disc vs USB body) — a pure read of
  // the stored format, not a derivation (R26).
  const mediaType = typeof values.media_type === 'string' ? values.media_type : null;
  // Portal-only preset marker — a dust jacket rides on product_type=single_sheet,
  // so the mockup needs it to draw the wrap instead of a plain sheet.
  const sheetUse =
    typeof values[SHEET_USE_CONFIG_KEY] === 'string' ? (values[SHEET_USE_CONFIG_KEY] as string) : null;

  // Build readable spec lines for the SUMMARY_KEYS that are set.
  const specLines: { label: string; value: string }[] = [];
  for (const key of SUMMARY_KEYS) {
    if (variant === 'strip' && !STRIP_KEYS.has(key)) continue;
    const f = findField(schema, key);
    if (!f) continue;
    // Opt-in booleans (self_cover) read back only when TRUE — a "No" would be
    // pure noise. Everything else falls through to displayValue.
    if (BOOLEAN_ONLY_WHEN_TRUE.has(key) && values[key] !== true) continue;
    let v = displayValue(f, summaryValue(key, values), SHORTEN_VALUE_KEYS.has(key));
    if (v == null) continue;
    // Custom-size escape: the dimensions the customer typed live in a sibling
    // `<key>_custom` config key (see SizeGrid). Read them back inline so the
    // summary shows "Custom size — 8″ × 9″", not a bare "Custom size". Scoped to
    // the finished-size fields (the only ones that write a `_custom` companion)
    // so a non-size enum that happens to carry a 'custom' option value — e.g.
    // coil_color — never picks up a stray annotation.
    if (isFinishedSizeField(key) && values[key] === 'custom') {
      const custom = values[`${key}_custom`];
      if (typeof custom === 'string' && custom.trim() !== '') {
        // Tidy "8x9" → "8″ × 9″" so the custom escape reads back in the catalog
        // glyph style. Idempotent, so a value already normalized on blur in
        // SizeGrid (and an older quote storing raw text) both render the same.
        v = `${v}: ${formatCustomSize(custom)}`;
      }
    }
    // An override (from INSIDE_PAGES_RELABEL or SUMMARY_LABELS) is translated,
    // keyed on the stable schema field `key`, with the English override as the
    // defaultValue fallback. Falling through to the schema's own field label
    // (f.label) stays untranslated — that's the DB-mastered print vocabulary,
    // out of scope for this extraction.
    const override = (insidePages ? INSIDE_PAGES_RELABEL[key] : undefined) ?? SUMMARY_LABELS[key];
    const label = override !== undefined ? t(`summary.label.${key}`, { defaultValue: override }) : f.label;
    specLines.push({ label, value: v });
  }

  // A book's inserts read back as one row naming each piece and where it goes —
  // the Inserts tag below says only that there are some. Rail only: the review
  // row keeps to the specs that tell two items apart.
  const insertsField = variant === 'strip' ? undefined : findField(schema, INSERTS_CONFIG_KEY);
  if (insertsField && fieldVisible(insertsField, values)) {
    const inserts = insertsSummaryText(values[INSERTS_CONFIG_KEY], t);
    if (inserts) {
      specLines.push({
        label: t(`summary.label.${INSERTS_CONFIG_KEY}`, { defaultValue: 'Inserts' }),
        value: inserts,
      });
    }
  }

  if (assembly) {
    if (kitRecap.componentsPerKit !== null) {
      specLines.push({
        label: t('orderServices.recap.rowComponentsPerKit'),
        value: formatNumber(kitRecap.componentsPerKit),
      });
    }
    specLines.push(...kitRecap.rows);
  }

  // Finishing selections read back as titled tag groups (Finishing touches →
  // Specialty Finishing → Proof), in form order — see buildTagGroups. The
  // review row omits them (STRIP_KEYS above).
  const tagGroups = variant === 'strip' ? [] : buildTagGroups(schema, values);
  const rows = variant === 'strip' ? specLines.slice(0, STRIP_ROW_LIMIT) : specLines;

  return (
    <Root className={variant === 'strip' ? 'spec-summary spec-summary--strip' : 'spec-summary'}>
      {variant === 'rail' && (
        <div
          className={
            family === 'assembly' && artworkUrl
              ? 'spec-summary__art spec-summary__art--photo'
              : 'spec-summary__art'
          }
          aria-hidden="true"
        >
          <ProjectMockup
            family={family}
            artworkUrl={artworkUrl}
            sheetAspect={sheetAspectRatio}
            mediaType={mediaType}
            sheetUse={sheetUse}
          />
        </div>
      )}
      <div className="spec-summary__head">
        <div className="spec-summary__family">
          {eyebrow ? `${eyebrow} · ${familyLabel(family)}` : familyLabel(family)}
        </div>
        <div className="spec-summary__title">{title || t('shared.untitledProject')}</div>
        <div className="spec-summary__qty">
          {t('summary.quantityLabel')} <strong>{formatNumber(quantity)}</strong>
        </div>
      </div>
      {rows.length > 0 && (
        <dl className="spec-summary__list">
          {rows.map((s, i) => (
            <div key={`${i}:${s.label}`} className="spec-summary__row">
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {tagGroups.map((g) => (
        <div key={g.id} className="spec-summary__tag-group">
          <div className="spec-summary__tag-group-title">
            {t(`summary.tagGroup.${g.id}`, { defaultValue: g.title })}
          </div>
          <div className="spec-summary__flags">
            {g.tags.map((tag) => (
              <span key={tag} className="spec-summary__flag">{tag}</span>
            ))}
          </div>
        </div>
      ))}
      {rows.length === 0 && tagGroups.length === 0 && (
        <p className="spec-summary__empty">
          {t('summary.emptyState')}
        </p>
      )}
    </Root>
  );
}
